/**
 * Purpose:
 *   Container component for toast notifications with fixed positioning.
 *
 * Notes:
 *   - Fixed position: full width on mobile, right column on desktop
 *   - Keyboard: ESC closes most recent toast
 */

'use client';

import React, { useCallback } from 'react';
import { ToastItem } from './ToastItem';
import type { ToastInternal } from '../context/ToastContext';
import { toastViewportStyles } from '../styles/ToastViewport.styles';

export interface IToastViewportProps {
  toasts: ToastInternal[];
  onDismiss: (id: string) => void;
}
export const ToastViewport: React.FC<IToastViewportProps> = ({ toasts, onDismiss }) => {
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape' && toasts[0]) onDismiss(toasts[0].id);
    },
    [toasts, onDismiss]
  );

  return (
    <div
      role="region"
      aria-label="Notifications"
      tabIndex={-1}
      onKeyDown={onKeyDown}
      className={toastViewportStyles.container}
    >
      {toasts.map((t) => (
        <div key={t.id} className={toastViewportStyles.itemWrapper}>
          <ToastItem toast={t} onClose={() => onDismiss(t.id)} />
        </div>
      ))}
    </div>
  );
};
